import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X,
  CircleNotch,
  Receipt,
  Bank,
  Warning,
  Coins,
  CalendarBlank,
  Eraser,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  createPayment,
  fetchOpenInvoices,
  PAYMENT_METHOD_LABELS,
  type OpenInvoice,
  type Payment,
  type PaymentMethod,
} from '@/lib/paymentsApi';
import { fetchContacts } from '@/lib/contactsApi';
import { fetchBankAccounts } from '@/lib/bankAccountsApi';
import { getApiErrorMessage } from '@/lib/api';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { ScrollLock } from '@/components/shared/ScrollLock';

type PaymentMode = 'payment' | 'installment' | 'write_off';

const MODE_CONFIG: Record<PaymentMode, { label: string; description: string; icon: React.ReactNode }> = {
  payment: {
    label: 'Payment',
    description: 'Full or partial cash received',
    icon: <Coins size={14} weight="duotone" />,
  },
  installment: {
    label: 'Installment',
    description: 'First or next payment in a series',
    icon: <CalendarBlank size={14} weight="duotone" />,
  },
  write_off: {
    label: 'Write-off',
    description: 'Clear the balance — no cash received',
    icon: <Eraser size={14} weight="duotone" />,
  },
};

interface Props {
  /** Pre-select an invoice to allocate against (used from invoice detail). */
  presetFeeNoteId?: string;
  /** Pre-select a matter to scope open invoices to. */
  presetMatterId?: string;
  onClose: () => void;
  onSaved: (p: Payment) => void;
}

interface AllocationDraft {
  feeNoteId: string;
  amountApplied: string;
  isWriteOff: boolean;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function fmtMoney(value: number, currency = 'GBP') {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value);
}

export function RecordPaymentModal({
  presetFeeNoteId,
  presetMatterId,
  onClose,
  onSaved,
}: Props) {
  const qc = useQueryClient();

  const [paymentMode, setPaymentMode] = useState<PaymentMode>('payment');
  const [contactId, setContactId] = useState<string | null>(null);
  const [payerName, setPayerName] = useState('');
  const [paymentDate, setPaymentDate] = useState(todayIso());
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('bank_transfer');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [depositAccountId, setDepositAccountId] = useState<string | null>(null);
  const [totalAmount, setTotalAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [installmentNextDue, setInstallmentNextDue] = useState('');
  const [allocations, setAllocations] = useState<AllocationDraft[]>([]);

  const isWriteOffMode = paymentMode === 'write_off';
  const isInstallmentMode = paymentMode === 'installment';

  // ─── data sources ───
  const contactsQ = useQuery({
    queryKey: ['contacts'],
    queryFn: fetchContacts,
  });

  const bankAccountsQ = useQuery({
    queryKey: ['bank-accounts', { isActive: 'true' }],
    queryFn: () => fetchBankAccounts({ isActive: 'true' }),
  });

  const invoicesQ = useQuery({
    queryKey: ['open-invoices', { contactId, matterId: presetMatterId }],
    queryFn: () =>
      fetchOpenInvoices({
        ...(contactId ? { contactId } : {}),
        ...(presetMatterId ? { caseId: presetMatterId } : {}),
      }),
    enabled: Boolean(contactId) || Boolean(presetMatterId),
  });

  // ─── default deposit account ───
  // Pre-select the chambers' default operating account on first load.
  useEffect(() => {
    if (depositAccountId || !bankAccountsQ.data) return;
    const operating = bankAccountsQ.data.find((a) => a.type === 'operating' && a.isDefault);
    if (operating) setDepositAccountId(operating.id);
    else if (bankAccountsQ.data.length > 0) {
      // Fall back to the first active account.
      setDepositAccountId(bankAccountsQ.data[0]!.id);
    }
  }, [bankAccountsQ.data, depositAccountId]);

  // ─── pre-select invoice + amount on first load if provided ───
  useEffect(() => {
    if (!presetFeeNoteId || !invoicesQ.data) return;
    if (allocations.find((a) => a.feeNoteId === presetFeeNoteId)) return;
    const inv = invoicesQ.data.find((i) => i.id === presetFeeNoteId);
    if (!inv) return;
    setAllocations([{ feeNoteId: presetFeeNoteId, amountApplied: inv.balance, isWriteOff: false }]);
    if (!totalAmount) setTotalAmount(inv.balance);
    // Auto-select the contact too for the firm linked to this matter.
    if (!contactId && inv.case?.instructingSolicitorId) {
      setContactId(inv.case.instructingSolicitorId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetFeeNoteId, invoicesQ.data]);

  // ─── write-off mode: auto-apply full balance + isWriteOff to all selected invoices ───
  useEffect(() => {
    if (!isWriteOffMode || !invoicesQ.data) return;
    setTotalAmount('0');
    setAllocations((prev) =>
      prev.map((a) => {
        const inv = invoicesQ.data!.find((i) => i.id === a.feeNoteId);
        return { ...a, amountApplied: inv ? inv.balance : a.amountApplied, isWriteOff: true };
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWriteOffMode]);

  // ─── derived numbers ───
  const total = parseFloat(totalAmount || '0') || 0;
  const allocationSum = useMemo(
    () => allocations.reduce((acc, a) => acc + (parseFloat(a.amountApplied) || 0), 0),
    [allocations],
  );
  const writeOffSum = useMemo(
    () =>
      allocations
        .filter((a) => a.isWriteOff)
        .reduce((acc, a) => acc + (parseFloat(a.amountApplied) || 0), 0),
    [allocations],
  );
  const cashSum = allocationSum - writeOffSum;
  const remainingToAllocate = total - cashSum;
  const overAllocated = allocationSum > total + 0.005;

  // ─── why is the submit button disabled? ───
  // The button guards silently on several conditions; surfacing the specific
  // reason turns a dead-end popup into a guided next step (e.g. a draft fee note
  // is not a payable invoice — it must be issued first).
  const hasInvoiceContext = Boolean(contactId || presetMatterId);
  const openInvoiceCount = invoicesQ.data?.length ?? 0;
  const disabledReason: string | null = !hasInvoiceContext
    ? 'Select the paying firm above to load its open invoices.'
    : invoicesQ.isLoading
      ? null
      : openInvoiceCount === 0
        ? 'No issued invoice to pay. Send (issue) the fee note first — a draft can’t be paid.'
        : allocations.length === 0
          ? 'Tick an invoice under “Apply to invoices” to allocate this payment.'
          : !isWriteOffMode && total <= 0
            ? 'Enter the payment amount received.'
            : overAllocated
              ? 'Allocations exceed the payment total.'
              : null;

  // ─── helpers ───
  const toggleInvoice = (inv: OpenInvoice) => {
    setAllocations((prev) => {
      const existing = prev.find((a) => a.feeNoteId === inv.id);
      if (existing) {
        return prev.filter((a) => a.feeNoteId !== inv.id);
      }
      if (isWriteOffMode) {
        // Write-off: always apply full outstanding balance and mark as write-off.
        return [
          ...prev,
          { feeNoteId: inv.id, amountApplied: inv.balance, isWriteOff: true },
        ];
      }
      const remainingOnPayment = Math.max(0, total - allocationSum);
      const balance = parseFloat(inv.balance) || 0;
      const suggested = total > 0 ? Math.min(balance, remainingOnPayment) : balance;
      return [
        ...prev,
        { feeNoteId: inv.id, amountApplied: suggested.toFixed(2), isWriteOff: false },
      ];
    });
  };

  const updateAllocation = (
    feeNoteId: string,
    patch: Partial<Pick<AllocationDraft, 'amountApplied' | 'isWriteOff'>>,
  ) => {
    setAllocations((prev) =>
      prev.map((a) => (a.feeNoteId === feeNoteId ? { ...a, ...patch } : a)),
    );
  };

  const saveMut = useMutation({
    mutationFn: async (): Promise<Payment> => {
      if (allocations.length === 0) throw new Error('Select at least one invoice to allocate to');
      if (!paymentDate) throw new Error('Pick a payment date');

      if (isWriteOffMode) {
        // Write-off: no cash received — total is 0, all allocations are write-offs.
        if (allocations.some((a) => !a.isWriteOff)) throw new Error('All allocations must be write-offs in write-off mode');
        return createPayment({
          paidByContactId: contactId,
          payerName: payerName.trim() || null,
          paymentDate,
          totalAmount: 0,
          currency: 'GBP',
          paymentMethod: 'other',
          referenceNumber: referenceNumber.trim() || null,
          depositAccountId: null,
          notes: `Write-off recorded${notes.trim() ? '. ' + notes.trim() : '.'}`,
          allocations: allocations.map((a) => ({
            feeNoteId: a.feeNoteId,
            amountApplied: parseFloat(a.amountApplied) || 0,
            isWriteOff: true,
          })),
        });
      }

      if (!total || total <= 0) throw new Error('Enter a payment total');
      if (overAllocated) throw new Error('Allocations exceed the payment total');

      // Installment: partial cash payment with next-due note.
      let resolvedNotes = notes.trim() || null;
      if (isInstallmentMode && installmentNextDue) {
        const nextDueLabel = new Date(installmentNextDue).toLocaleDateString('en-GB', {
          day: 'numeric', month: 'long', year: 'numeric',
        });
        resolvedNotes = `Installment payment — next payment due: ${nextDueLabel}.${notes.trim() ? ' ' + notes.trim() : ''}`;
      }

      return createPayment({
        paidByContactId: contactId,
        payerName: payerName.trim() || null,
        paymentDate,
        totalAmount: total,
        currency: 'GBP',
        paymentMethod,
        referenceNumber: referenceNumber.trim() || null,
        depositAccountId,
        notes: resolvedNotes,
        allocations: allocations.map((a) => ({
          feeNoteId: a.feeNoteId,
          amountApplied: parseFloat(a.amountApplied) || 0,
          isWriteOff: a.isWriteOff,
        })),
      });
    },
    onSuccess: (p) => {
      const successMsg = isWriteOffMode
        ? 'Write-off recorded'
        : isInstallmentMode
        ? 'Installment payment recorded'
        : 'Payment recorded';
      toast.success(successMsg);
      // ['payments'] is read by NOTHING — the matter's payments list is ['matter-payments', id],
      // so a payment you just recorded never appeared on the tab you recorded it from.
      qc.invalidateQueries({ queryKey: ['matter-payments'] });
      qc.invalidateQueries({ queryKey: ['matter-fees-activities'] });
      qc.invalidateQueries({ queryKey: ['open-invoices'] });
      qc.invalidateQueries({ queryKey: ['matter-fees'] }); // ['fees'] had no reader
      qc.invalidateQueries({ queryKey: ['fee-notes'] });
      qc.invalidateQueries({ queryKey: ['matter-financial-summary'] });
      qc.invalidateQueries({ queryKey: ['aged-debt'] });
      onSaved(p);
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Could not record payment'));
    },
  });

  const firmContacts = useMemo(() => {
    return (contactsQ.data ?? []).filter(
      (c) => c.type === 'firm' || c.type === 'solicitor' || c.type === 'company',
    );
  }, [contactsQ.data]);

  const contactLabel = (c: { name: string; org: string }): string => {
    if (c.org) return c.org;
    return c.name || 'Unnamed contact';
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-6"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saveMut.isPending) onClose();
      }}
    >
      <ScrollLock />
      <Card className="my-auto flex max-h-full w-full max-w-3xl flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight">
              <Receipt size={15} weight="duotone" className="text-brand-600 dark:text-brand-400" />
              Record payment
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Log a payment received and apply it to one or more open invoices.
            </p>
          </div>
          <button
            type="button"
            onClick={() => !saveMut.isPending && onClose()}
            disabled={saveMut.isPending}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {/* ── Payment type selector ── */}
          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              Payment type
            </label>
            <div className="flex gap-1 rounded-lg border border-border bg-muted/30 p-1">
              {(Object.entries(MODE_CONFIG) as [PaymentMode, typeof MODE_CONFIG[PaymentMode]][]).map(([mode, cfg]) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => {
                    setPaymentMode(mode);
                    // Reset write-off flags when leaving write-off mode
                    if (mode !== 'write_off') {
                      setAllocations((prev) => prev.map((a) => ({ ...a, isWriteOff: false })));
                      if (mode !== 'payment' || totalAmount === '0') setTotalAmount('');
                    }
                  }}
                  disabled={saveMut.isPending}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-2 text-xs font-medium transition-all ${
                    paymentMode === mode
                      ? mode === 'write_off'
                        ? 'bg-amber-600 text-white shadow-sm'
                        : 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {cfg.icon}
                  <span>{cfg.label}</span>
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {MODE_CONFIG[paymentMode].description}
            </p>
          </div>

          {/* ── Write-off warning ── */}
          {isWriteOffMode && (
            <div className="flex items-start gap-2.5 rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50/70 dark:bg-amber-500/10 px-3 py-2.5">
              <Warning size={14} weight="fill" className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="text-xs text-amber-800 dark:text-amber-300">
                <span className="font-semibold">No cash will be received.</span>{' '}
                The outstanding balance will be cleared and the invoice closed as written-off. This action cannot be undone without voiding the payment record.
              </div>
            </div>
          )}

          {/* Payer + payment basics */}
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                Paid by — firm contact
              </label>
              <select
                value={contactId ?? ''}
                onChange={(e) => {
                  setContactId(e.target.value || null);
                  setAllocations([]); // reset when firm changes
                }}
                disabled={saveMut.isPending}
                className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              >
                <option value="">— Choose a firm —</option>
                {firmContacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {contactLabel(c)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                Payer name <span className="font-normal normal-case text-muted-foreground/70">(optional)</span>
              </label>
              <Input
                value={payerName}
                onChange={(e) => setPayerName(e.target.value)}
                placeholder="If different from the firm — e.g. cheque drawer"
                maxLength={200}
                disabled={saveMut.isPending}
              />
            </div>

            {!isWriteOffMode && (
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  {isInstallmentMode ? 'Instalment amount (£)' : 'Payment total (£)'}
                </label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  value={totalAmount}
                  onChange={(e) => setTotalAmount(e.target.value)}
                  placeholder="0.00"
                  disabled={saveMut.isPending}
                />
              </div>
            )}
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                Payment date
              </label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                disabled={saveMut.isPending}
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                Payment method
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                disabled={saveMut.isPending}
                className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              >
                {Object.entries(PAYMENT_METHOD_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                Reference number <span className="font-normal normal-case text-muted-foreground/70">(optional)</span>
              </label>
              <Input
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                placeholder="Cheque #, bank ref, transaction ID"
                maxLength={100}
                disabled={saveMut.isPending}
              />
            </div>

            {!isWriteOffMode && (
              <div className="md:col-span-2">
                <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  Deposit account
                </label>
                <select
                  value={depositAccountId ?? ''}
                  onChange={(e) => setDepositAccountId(e.target.value || null)}
                  disabled={saveMut.isPending}
                  className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                >
                  <option value="">— No deposit account selected —</option>
                  {bankAccountsQ.data?.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.type === 'operating' ? '🏛️' : a.type === 'trust' ? '🛡️' : '🏦'} {a.name}
                      {a.accountNumberLast4 && ` (•••• ${a.accountNumberLast4})`}
                      {a.isDefault && ' — default'}
                    </option>
                  ))}
                </select>
                {bankAccountsQ.data && bankAccountsQ.data.length === 0 && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
                    <Warning size={11} weight="fill" />
                    No active bank accounts found. Add one in Bank Accounts first.
                  </p>
                )}
              </div>
            )}

            {isInstallmentMode && (
              <div className="md:col-span-2">
                <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  Next payment due <span className="font-normal normal-case text-muted-foreground/70">(optional)</span>
                </label>
                <Input
                  type="date"
                  value={installmentNextDue}
                  onChange={(e) => setInstallmentNextDue(e.target.value)}
                  disabled={saveMut.isPending}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  This date is recorded in the payment notes and surfaces in the recovery queue.
                </p>
              </div>
            )}
          </div>

          {/* Allocations */}
          <div className="border-t border-border pt-3">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-[0.06em]">
                Apply to invoices
              </h3>
              {(contactId || presetMatterId) && (
                <span className="text-xs text-muted-foreground">
                  {invoicesQ.data?.length ?? 0} open invoice
                  {(invoicesQ.data?.length ?? 0) === 1 ? '' : 's'}
                </span>
              )}
            </div>

            {!contactId && !presetMatterId && (
              <div className="rounded-md border border-dashed border-border bg-muted/30 px-3 py-4 text-center text-xs text-muted-foreground">
                Select a firm above to see their open invoices.
              </div>
            )}

            {(contactId || presetMatterId) && invoicesQ.isLoading && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <CircleNotch size={12} className="animate-spin" /> Loading open invoices…
              </div>
            )}

            {invoicesQ.data && invoicesQ.data.length === 0 && (contactId || presetMatterId) && (
              <div className="rounded-md border border-border bg-muted/30 px-3 py-3 text-xs text-muted-foreground">
                No open invoices for this firm. The payment can't be recorded without one.
              </div>
            )}

            {invoicesQ.data && invoicesQ.data.length > 0 && (
              <div className="overflow-hidden rounded-md border border-border">
                <table className="w-full text-[12.5px]">
                  <thead className="bg-muted/30 text-xs uppercase tracking-[0.06em] text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Apply</th>
                      <th className="px-3 py-2 text-left font-medium">Invoice</th>
                      <th className="px-3 py-2 text-left font-medium">Matter</th>
                      <th className="px-3 py-2 text-right font-medium">Total</th>
                      <th className="px-3 py-2 text-right font-medium">Outstanding</th>
                      <th className="px-3 py-2 text-right font-medium">
                        {isWriteOffMode ? 'Write-off £' : 'Apply £'}
                      </th>
                      {!isWriteOffMode && (
                        <th className="px-3 py-2 text-center font-medium">Write-off</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {invoicesQ.data.map((inv) => {
                      const allocation = allocations.find((a) => a.feeNoteId === inv.id);
                      const checked = Boolean(allocation);
                      return (
                        <tr
                          key={inv.id}
                          className={
                            checked
                              ? isWriteOffMode
                                ? 'bg-amber-50/60 dark:bg-amber-500/10'
                                : 'bg-brand-50/60'
                              : 'hover:bg-muted/20'
                          }
                        >
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleInvoice(inv)}
                              disabled={saveMut.isPending}
                              className={`h-3.5 w-3.5 cursor-pointer ${isWriteOffMode ? 'accent-amber-600' : 'accent-brand-600'}`}
                            />
                          </td>
                          <td className="px-3 py-2 text-xs">{inv.feeNoteNumber}</td>
                          <td className="px-3 py-2 truncate">
                            {inv.case?.caseNumber ?? '—'}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {fmtMoney(Number(inv.total), inv.currency)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {fmtMoney(Number(inv.balance), inv.currency)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {checked ? (
                              <input
                                type="number"
                                inputMode="decimal"
                                step="0.01"
                                value={allocation!.amountApplied}
                                onChange={(e) => updateAllocation(inv.id, { amountApplied: e.target.value })}
                                disabled={saveMut.isPending || isWriteOffMode}
                                className={`w-24 rounded-md border border-input bg-background px-2 py-1 text-right text-xs ${
                                  isWriteOffMode ? 'opacity-60' : ''
                                }`}
                              />
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          {!isWriteOffMode && (
                            <td className="px-3 py-2 text-center">
                              {checked ? (
                                <input
                                  type="checkbox"
                                  checked={allocation!.isWriteOff}
                                  onChange={(e) =>
                                    updateAllocation(inv.id, { isWriteOff: e.target.checked })
                                  }
                                  disabled={saveMut.isPending}
                                  title="Mark as write-off — reduces invoice balance without recording cash"
                                  className="h-3 w-3 cursor-pointer accent-amber-600"
                                />
                              ) : null}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Live summary */}
            {allocations.length > 0 && (
              <div className={`mt-3 rounded-md border px-3 py-2 text-xs ${
                isWriteOffMode
                  ? 'border-amber-200 dark:border-amber-500/30 bg-amber-50/50 dark:bg-amber-500/10'
                  : 'border-border bg-muted/30'
              }`}>
                {isWriteOffMode ? (
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-[0.06em] text-amber-700 dark:text-amber-300">Total to write off</div>
                    <div className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                      {fmtMoney(allocationSum)}
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground">Cash applied</div>
                      <div className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                        {fmtMoney(cashSum)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground">Write-offs</div>
                      <div className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                        {fmtMoney(writeOffSum)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground">Remaining</div>
                      <div
                        className={`text-sm font-semibold ${
                          overAllocated ? 'text-red-700 dark:text-red-300' : 'text-foreground'
                        }`}
                      >
                        {fmtMoney(remainingToAllocate)}
                      </div>
                    </div>
                    {overAllocated && (
                      <div className="col-span-3 mt-1 flex items-center gap-1 text-xs text-red-700 dark:text-red-300">
                        <Warning size={11} weight="fill" />
                        Allocation exceeds the payment total — adjust amounts to continue.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              Notes <span className="font-normal normal-case text-muted-foreground/70">(optional)</span>
            </label>
            <ExpandableTextarea
              label="Notes"
              value={notes}
              onChange={setNotes}
              placeholder="Internal notes — not shown to the client."
              rows={2}
              maxLength={2000}
              disabled={saveMut.isPending}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border px-5 py-3">
          <p className="min-w-0 flex-1 text-xs text-amber-700 dark:text-amber-300">
            {disabledReason}
          </p>
          <div className="flex shrink-0 items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saveMut.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => saveMut.mutate()}
            disabled={
              saveMut.isPending ||
              allocations.length === 0 ||
              (!isWriteOffMode && (total <= 0 || overAllocated))
            }
            className={isWriteOffMode ? 'gap-1.5 bg-amber-600 text-white hover:bg-amber-700' : 'gap-1.5'}
          >
            {saveMut.isPending ? (
              <CircleNotch size={14} className="animate-spin" />
            ) : isWriteOffMode ? (
              <Eraser size={14} weight="fill" />
            ) : (
              <Bank size={14} weight="fill" />
            )}
            {isWriteOffMode ? 'Record write-off' : isInstallmentMode ? 'Record instalment' : 'Record payment'}
          </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
